import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ShoppingCart, Search, Truck, Shield, RefreshCw } from 'lucide-react';
import { api } from '../../api';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice: number;
  category: string;
  rating: number;
  reviews: number;
  image: string;
  inStock: boolean;
  features: string[];
  badge: string | null;
  isValidated?: boolean;
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  rating: number;
  product: string;
}

const ShowcaseShop: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, testimonialsResponse] = await Promise.all([
          api.get<Product[]>('/siteContent/products'),
          api.get<Testimonial[]>('/siteContent/testimonials'),
        ]);
        setProducts((productsResponse.data || []).filter(p => p.isValidated !== false));
        setTestimonials(testimonialsResponse.data || []);
      } catch (err) {
        setError('Failed to fetch shop content.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = [
    { id: 'all', name: 'Tous les produits', count: products.length },
    { id: 'gps', name: 'GPS & Tracking', count: products.filter(p => p.category === 'gps').length },
    { id: 'cardio', name: 'Cardio & Santé', count: products.filter(p => p.category === 'cardio').length },
    { id: 'technology', name: 'Technologie', count: products.filter(p => p.category === 'technology').length },
    { id: 'equipment', name: 'Équipement', count: products.filter(p => p.category === 'equipment').length }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return <div>Loading shop content...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
     <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  Le <span className="text-[#FFB639]">Shop</span> FORMACADEMY
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Matériel validé par nos experts. Produits testés et sélectionnés pour les meilleurs rapports qualité/prix.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Découvrir les produits
                </button>
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300"
                >
                  Conseils personnalisés
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Truck className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Livraison gratuite dès 100€</span>
                </div>
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Garantie 2 ans</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.pexels.com/photos/163064/play-stone-network-networked-interactive-163064.jpeg"
                  alt="Matériel FORMACADEMY"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">100%</div>
                  <div className="text-sm">Testé & validé</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Produits testés</h3>
              <p className="text-gray-600">Chaque produit est testé et validé par nos experts avant d'être proposé</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Meilleur rapport qualité/prix</h3>
              <p className="text-gray-600">Sélection rigoureuse pour vous offrir le meilleur rapport qualité/prix du marché</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Conseil & accompagnement</h3>
              <p className="text-gray-600">Notre équipe vous accompagne dans le choix et l'utilisation de votre matériel</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
              />
            </div>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-[#FFB639] text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md border border-gray-200'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  {product.badge && (
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-semibold ${
                      product.badge === 'Bestseller' ? 'bg-[#FFB639] text-white' :
                      product.badge === 'Nouveau' ? 'bg-[#FFB639] text-white' :
                      'bg-[#FFB639]/100 text-white'
                    }`}>
                      {product.badge}
                    </div>
                  )}
                  {!product.inStock && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold">
                        Rupture de stock
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-[#FFB639] " />
                      <span className="text-sm font-medium text-gray-700 ml-1">{product.rating ?? 0}</span>
                      {typeof product.reviews === 'number' && (
                        <span className="text-sm text-gray-500 ml-1">({product.reviews})</span>
                      )}
                    </div>
                    <div className={`text-sm font-medium ${product.inStock ? 'text-[#FFB639]' : 'text-red-600'}`}>
                      {product.inStock ? 'En stock' : 'Rupture'}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {product.description}
                  </p>

                  <ul className="space-y-1 mb-4">
                    {(product.features || []).slice(0, 2).map((feature, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <div className="w-1.5 h-1.5 bg-[#FFB639] rounded-full mr-2"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    disabled={!product.inStock}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center ${
                      product.inStock
                        ? 'bg-[#FFB639] text-white hover:bg-[#FFB639]'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {product.inStock ? 'Ajouter au panier' : 'Indisponible'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
              <p className="text-gray-500">
                Essayez de modifier vos critères de recherche
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez les retours de nos clients professionnels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-[#FFB639] " />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic text-lg">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                  <div className="text-sm text-[#FFB639] font-medium">
                    {testimonial.product}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping & Returns */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Livraison & Retours
              </h2>
              <p className="text-xl text-gray-600">
                Service client de qualité pour votre tranquillité
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="h-8 w-8 text-[#FFB639]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Livraison rapide</h3>
                <p className="text-gray-600 mb-4">
                  Livraison gratuite dès 100€ d'achat. Expédition sous 24h pour les produits en stock.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Standard: 3-5 jours ouvrés</li>
                  <li>• Express: 24-48h</li>
                  <li>• Gratuite dès 100€</li>
                </ul>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="h-8 w-8 text-[#FFB639]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Retours faciles</h3>
                <p className="text-gray-600 mb-4">
                  30 jours pour changer d'avis. Retour gratuit et remboursement rapide.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 30 jours pour retourner</li>
                  <li>• Retour gratuit</li>
                  <li>• Remboursement sous 5 jours</li>
                </ul>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-[#FFB639]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Garantie étendue</h3>
                <p className="text-gray-600 mb-4">
                  Tous nos produits bénéficient d'une garantie constructeur étendue.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Garantie 2 ans minimum</li>
                  <li>• SAV réactif</li>
                  <li>• Pièces détachées disponibles</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Besoin de conseils personnalisés ?
          </h2>
          <p className="text-xl text-[#FFB639] mb-8 max-w-3xl mx-auto">
            Notre équipe d'experts vous accompagne dans le choix du matériel adapté à vos besoins
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105"
            >
              Demander un conseil
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
              Catalogue complet
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseShop;
